import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { StringParam, useQueryParam } from "use-query-params";

import {
  dispatchGetEventAction,
  dispatchGetEventVenueBlocks,
  eventBlocksSelectorCreator,
  eventSelectorCreator,
} from "Bands/state";
import { EventUniqueBlocks, IEvent, IVenueBlock } from "Bands/types";
import { buildUrl, mapUserPermissions } from "shared/utils";
import { BAND_PATHS } from "Bands/router";
import { getEventBandId } from "Bands/utils";

interface VenueContextState {
  eventId: string | null;
  event: IEvent | null;
  venueBlock: IVenueBlock | null;
  editBlockUrl: string | null;
  selectedStage: string | null;
  selectedCapacity: string | null;
}

const initialStage: VenueContextState = {
  eventId: null,
  event: null,
  venueBlock: null,
  editBlockUrl: null,
  selectedStage: null,
  selectedCapacity: null,
};

const VenueEventContext = createContext(initialStage);

export const VenueEventProvider: React.FC = ({ children }) => {
  const [eventId] = useQueryParam("eventId", StringParam);

  const dispatch = useDispatch();

  const eventIdSelector = useMemo(
    () => eventSelectorCreator(eventId || ""),
    [eventId]
  );
  const event = useSelector(eventIdSelector);

  const venueBlocksSelector = useMemo(
    () => eventBlocksSelectorCreator(EventUniqueBlocks.venue),
    []
  );
  const venueBlocks = useSelector(venueBlocksSelector) as IVenueBlock[];
  const venueBlock = useMemo(() => venueBlocks[0], [venueBlocks]);

  useEffect(() => {
    if (eventId) {
      dispatchGetEventAction(dispatch, eventId);
      dispatchGetEventVenueBlocks(dispatch, eventId);
    }
  }, [eventId, dispatch]);

  const selectedStage = useMemo(
    () => venueBlock?.stage?.id || null,
    [venueBlock]
  );
  const selectedCapacity = useMemo(
    () => venueBlock?.stageCapacity?.id || null,
    [venueBlock]
  );

  const bandId = useMemo(() => (event ? getEventBandId(event) : null), [event]);
  const editBlockUrl = useMemo(() => {
    if (
      !bandId ||
      !eventId ||
      !venueBlock ||
      !mapUserPermissions(venueBlock.permissions || []).EDIT
    )
      return null;

    return buildUrl(BAND_PATHS.EDIT_VENUE_BLOCK, {
      bandId,
      eventId,
      blockId: venueBlock.id,
    });
  }, [bandId, venueBlock, eventId]);

  return (
    <VenueEventContext.Provider
      value={{
        eventId: eventId ?? null,
        event,
        venueBlock,
        editBlockUrl,
        selectedStage,
        selectedCapacity,
      }}
    >
      {children}
    </VenueEventContext.Provider>
  );
};

export const useVenueEventContext = () => useContext(VenueEventContext);
